import { useState } from 'react';
import { useParams } from 'react-router';
import { Section, Wrap, SectionIntro } from '../components/ui/Section.jsx';
import { Sequence, Reveal, LineRise } from '../components/ui/motion.jsx';
import { Eyebrow } from '../components/ui/Eyebrow.jsx';
import { BackLink } from '../components/ui/BackLink.jsx';
import { SpecList } from '../components/ui/SpecList.jsx';
import { PlaceholderArt } from '../components/ui/PlaceholderArt.jsx';
import { Button } from '../components/ui/Button.jsx';
import { CtaBanner } from '../components/ui/CtaBanner.jsx';
import { Lightbox } from '../features/lightbox/Lightbox.jsx';
import { ZoomableImage } from '../features/lightbox/ZoomableImage.jsx';
import { useReveal, revealState } from '../hooks/useReveal.js';
import { getProject } from '../data/projects.js';
import NotFoundPage from './NotFoundPage.jsx';
import styles from './ProjectDetailPage.module.css';

/** Route-level metadata, resolved per slug by the router config. */
export function metaFor(slug) {
  const project = getProject(slug);
  if (!project) return { title: 'Project not found — Abode Custom Homes', noindex: true };
  return {
    title: `${project.title} — Abode Custom Homes`,
    description: project.lede,
  };
}

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const project = getProject(slug);
  const [openIndex, setOpenIndex] = useState(null);
  const [figureRef, figureShown] = useReveal({ mode: 'block' });

  // A slug with no record, or one with no page written for it yet, is a 404
  // rather than a half-empty page.
  if (!project || !project.body) return <NotFoundPage />;

  // The hero image and the gallery form one ordered set, so they page
  // through as a single sequence in the viewer.
  const lightboxItems = [
    { src: project.image, alt: project.imageAlt, caption: project.heroCaption },
    ...project.gallery,
  ];

  return (
    <>
      <section className={styles.hero}>
        <Wrap>
          <BackLink to="/projects" className={styles.backLink}>All projects</BackLink>
          <div className={styles.head}>
            <div>
              <Eyebrow animated={false}>{project.eyebrow}</Eyebrow>
              <LineRise
                as="h1"
                className="h1 mt-2"
                onLoad
                lines={project.titleLines ?? [project.title]}
              />
              <address className={styles.address} data-enter="up">
                {project.address}
              </address>
            </div>
            <p className="lede" data-enter="up">{project.lede}</p>
          </div>
        </Wrap>
      </section>

      <Section padding="tight">
        <Wrap>
          <figure className={styles.figure}>
            <ZoomableImage
              ref={figureRef}
              data-figure-rise={revealState(figureShown)}
              src={project.image}
              alt={project.imageAlt}
              caption={project.heroCaption}
              onOpen={() => setOpenIndex(0)}
            />
          </figure>
        </Wrap>
      </Section>

      <Section>
        <Wrap>
          <div className={styles.detail}>
            <Reveal className={styles.copy}>
              <h2 className="h2">About this home</h2>
              {project.body.map((paragraph) => (
                <p key={paragraph.slice(0, 32)}>{paragraph}</p>
              ))}
            </Reveal>

            <Sequence as="aside" aria-labelledby="specs-heading">
              <Eyebrow as="h2" id="specs-heading">Specifications</Eyebrow>
              <SpecList specs={project.specs} />
              {/* The direct line, above the listing link: an inquiry that
                  reaches Abode is worth more than a click through to
                  Zillow, where the competition is one scroll away. The
                  project slug travels in the query string and the contact
                  form turns it into a prefilled message — the wording
                  stays in the code rather than the URL, so nobody can hand
                  out a link that makes the site say something else. */}
              <Button
                to={`/contact?project=${project.slug}`}
                variant="primary"
                className="mt-4"
              >
                Ask about this home
              </Button>

              {/* Rendered only once there is a real listing to point at, so
                  the page never ships a dead control. */}
              {project.listingUrl ? (
                <Button
                  href={project.listingUrl}
                  variant="ghost"
                  icon="externalLink"
                  className="mt-2"
                >
                  View on Zillow
                </Button>
              ) : null}
            </Sequence>
          </div>
        </Wrap>
      </Section>

      <Section tone="alt">
        <Wrap>
          <Reveal as={SectionIntro}>
            <div>
              <Eyebrow animated={false}>Gallery</Eyebrow>
              <h2 className="h2 mt-2">More of the house.</h2>
            </div>
          </Reveal>

          <GalleryGrid
            project={project}
            onOpen={(i) => setOpenIndex(i + 1)}
          />
        </Wrap>
      </Section>

      <Section padding="tight">
        <Wrap>
          <CtaBanner lines={['Want something', 'like this?']} />
        </Wrap>
      </Section>

      <Lightbox
        items={lightboxItems}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onIndexChange={setOpenIndex}
      />
    </>
  );
}

/**
 * Real images when they exist, honest placeholders until they do. Three
 * tiles either way, so the section keeps its shape as photos arrive.
 */
function GalleryGrid({ project, onOpen }) {
  const [ref, shown] = useReveal({ mode: 'section' });
  const hasImages = project.gallery.length > 0;
  const tiles = hasImages ? project.gallery : [0, 1, 2];

  return (
    <div ref={ref} className={styles.gallery}>
      {tiles.map((tile, i) => (
        <div
          key={hasImages ? tile.src : i}
          style={{ '--i': i }}
          data-gallery-item={revealState(shown)}
        >
          {hasImages ? (
            <ZoomableImage
              src={tile.src}
              alt={tile.alt}
              caption={tile.caption}
              onOpen={() => onOpen(i)}
            />
          ) : (
            <PlaceholderArt
              label="More images coming soon"
              icon={i === 1 ? 'home' : 'house'}
            />
          )}
        </div>
      ))}
    </div>
  );
}
